import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Button } from "@components/Button";
import { TextInput } from "@components/Input";
import { MainLayout } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { BodySmall } from "@components/Typography";
import { useErrorBanner, useSetUserTrustedContact, useUser } from "@hooks";
import { i18n, Translations } from "@i18n";
import { constants } from "@layout";
import { formatPhoneNumber, sanitizePhoneNumber } from "@utils";
import { validationSchema } from "./content";
import { TrustedContactFormValues } from "./types";

export const TrustedContactRoute = () => {
  const { user } = useUser();
  const { showErrorBanner } = useErrorBanner();
  const navigate = useNavigate();

  const existingEmail = user?.userFinancialProfile?.trustedContactEmail;
  const existingPhoneNumber = user?.userFinancialProfile?.trustedContactPhoneNumber;
  const existingFirstName = user?.userFinancialProfile?.trustedContactFirstName;
  const existingLastName = user?.userFinancialProfile?.trustedContactLastName;

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm<TrustedContactFormValues>({
    defaultValues: {
      firstName: existingFirstName ?? "",
      lastName: existingLastName ?? "",
      email: existingEmail ?? "",
      phoneNumber: existingPhoneNumber ? formatPhoneNumber(existingPhoneNumber) : "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const [setUserTrustedContact, { loading }] = useSetUserTrustedContact();

  const handleSubmit = async ({
    firstName,
    lastName,
    email,
    phoneNumber,
  }: TrustedContactFormValues) => {
    try {
      const sanitizedPhoneNumber = sanitizePhoneNumber(phoneNumber);

      await setUserTrustedContact({
        variables: {
          trustedContactFirstName: firstName,
          trustedContactLastName: lastName,
          trustedContactEmail: email,
          trustedContactPhoneNumber: sanitizedPhoneNumber,
        },
      });

      navigate(-1);
    } catch {
      showErrorBanner();
    }
  };

  const firstNameController = register("firstName");
  const lastNameController = register("lastName");
  const emailController = register("email");
  const phoneNumberController = register("phoneNumber");

  const onChangeFirstNameText = (value: string) => {
    setValue("firstName", value, constants.form);
  };

  const onChangeLastNameText = (value: string) => {
    setValue("lastName", value, constants.form);
  };

  const onChangeEmailText = (value: string) => {
    setValue("email", value, constants.form);
  };

  const onChangePhoneNumberText = (value: string) => {
    const formattedPhoneNumber = formatPhoneNumber(value);
    setValue("phoneNumber", formattedPhoneNumber, constants.form);
  };

  return (
    <MainLayout pageHeader={i18n.t(Translations.TrustedContactRouteHeader)}>
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...firstNameController}
        label={i18n.t(Translations.TrustedContactRouteInputFirstNameLabel)}
        placeholder={i18n.t(Translations.TrustedContactRouteInputFirstNamePlaceholder)}
        value={watch("firstName")}
        onChangeText={onChangeFirstNameText}
        error={errors.firstName?.message}
      />

      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...lastNameController}
        label={i18n.t(Translations.TrustedContactRouteInputLastNameLabel)}
        placeholder={i18n.t(Translations.TrustedContactRouteInputLastNamePlaceholder)}
        value={watch("lastName")}
        onChangeText={onChangeLastNameText}
        error={errors.lastName?.message}
      />

      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...emailController}
        label={i18n.t(Translations.TrustedContactRouteInputEmailLabel)}
        placeholder={i18n.t(Translations.TrustedContactRouteInputEmailPlaceholder)}
        value={watch("email")}
        onChangeText={onChangeEmailText}
        error={errors.email?.message}
      />

      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...phoneNumberController}
        label={i18n.t(Translations.TrustedContactRouteInputPhoneNumberLabel)}
        placeholder={i18n.t(Translations.TrustedContactRouteInputPhoneNumberPlaceholder)}
        value={watch("phoneNumber")}
        onChangeText={onChangePhoneNumberText}
        error={errors.phoneNumber?.message}
      />

      <Spacing size={SpacingSizes.M} />

      <BodySmall>{i18n.t(Translations.TrustedContactRouteFooter)}</BodySmall>

      <Spacing size={SpacingSizes.M} />

      <Button
        title={i18n.t(Translations.TrustedContactRoutePrimaryCTA)}
        onClick={handleFormSubmit(handleSubmit)}
        disabled={!isValid || !isDirty}
        loading={loading}
      />

      <Spacing size={SpacingSizes.M} />
    </MainLayout>
  );
};
